// Lux Final Spark VFX — three-phase laser beam mimicking LoL's R ability.
// Phase 1 (0–400ms):   A thin light-line charges from caster toward target (windup).
// Phase 2 (400–900ms): The beam erupts to full width with glowing core + outer glow.
// Phase 3 (900–1300ms): Beam fades out, leaving a lingering afterglow.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const WINDUP_END = 400;
const ACTIVE_END = 900;
const FADE_END = 1300;
const TOTAL_MS = FADE_END;

interface Mote {
  t: number;       // spawn time (ms)
  ox: number;      // offset from beam center (perp)
  dist: number;    // position along the beam (0–1)
  size: number;
  speed: number;   // perpendicular drift speed px/s
  alpha: number;
  life: number;    // lifetime ms
}

export class LuxBeamVFX implements Animation {
  private elapsed = 0;
  private motes: Mote[] = [];
  private nextMoteAt = 0;

  constructor(
    private from: Point,
    private to: Point,
  ) {}

  update(dt: number): void {
    this.elapsed += dt;

    // Spawn sparkling motes during active phase
    if (this.elapsed >= WINDUP_END && this.elapsed < ACTIVE_END) {
      if (this.elapsed >= this.nextMoteAt) {
        this.nextMoteAt = this.elapsed + 18 + Math.random() * 22;
        this.motes.push({
          t: this.elapsed,
          ox: (Math.random() - 0.5) * 14,
          dist: 0.1 + Math.random() * 0.85,
          size: 1.5 + Math.random() * 3,
          speed: (Math.random() - 0.5) * 90,
          alpha: 0.6 + Math.random() * 0.4,
          life: 280 + Math.random() * 200,
        });
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed;
    const dx = this.to.x - this.from.x;
    const dy = this.to.y - this.from.y;
    const len = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx);

    ctx.save();
    ctx.translate(this.from.x, this.from.y);
    ctx.rotate(angle);

    if (t < WINDUP_END) {
      // ── Phase 1: charging line ──
      const p = Easing.easeOutQuad(t / WINDUP_END);
      const travelLen = len * p;

      // Outer soft glow trace
      ctx.globalAlpha = 0.25 + p * 0.25;
      ctx.strokeStyle = "#c084fc";
      ctx.lineWidth = 4;
      ctx.shadowColor = "#a855f7";
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(travelLen, 0);
      ctx.stroke();

      // Bright core
      ctx.globalAlpha = 0.7 + p * 0.3;
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(travelLen, 0);
      ctx.stroke();

      // Leading energy ball
      const ballR = 5 + p * 4;
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = "#e9d5ff";
      ctx.shadowColor = "#d8b4fe";
      ctx.shadowBlur = 30;
      ctx.beginPath();
      ctx.arc(travelLen, 0, ballR, 0, Math.PI * 2);
      ctx.fill();
    } else if (t < ACTIVE_END) {
      // ── Phase 2: full beam eruption ──
      const p = (t - WINDUP_END) / (ACTIVE_END - WINDUP_END);
      const widthP = Math.sin(Math.PI * p); // rises then falls within this phase
      const outerW = 36 * widthP + 8;
      const midW = 18 * widthP + 4;
      const coreW = 6 * widthP + 2;
      const alpha = 1 - p * 0.35;

      // Outer diffuse glow
      ctx.globalAlpha = alpha * 0.22;
      ctx.fillStyle = "#a855f7";
      ctx.shadowColor = "#c084fc";
      ctx.shadowBlur = 40;
      ctx.fillRect(0, -outerW / 2, len, outerW);

      // Mid purple layer
      ctx.globalAlpha = alpha * 0.55;
      ctx.fillStyle = "#d8b4fe";
      ctx.shadowColor = "#e879f9";
      ctx.shadowBlur = 24;
      ctx.fillRect(0, -midW / 2, len, midW);

      // White-gold core
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#fef9c3";
      ctx.shadowColor = "#fde047";
      ctx.shadowBlur = 12;
      ctx.fillRect(0, -coreW / 2, len, coreW);

      // Bright white centre line
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#ffffff";
      ctx.shadowBlur = 6;
      ctx.fillRect(0, -1.5, len, 3);

      // Impact flash at end of beam
      const flashR = 18 * widthP;
      ctx.globalAlpha = alpha * 0.85;
      ctx.fillStyle = "#fef08a";
      ctx.shadowColor = "#fde047";
      ctx.shadowBlur = 40;
      ctx.beginPath();
      ctx.arc(len, 0, flashR, 0, Math.PI * 2);
      ctx.fill();

      // Draw motes (in rotated space they travel perpendicular to beam)
      for (const m of this.motes) {
        const age = t - m.t;
        if (age < 0 || age > m.life) continue;
        const mp = age / m.life;
        const mx = m.dist * len;
        const my = m.ox + (m.speed * age) / 1000;
        ctx.globalAlpha = m.alpha * (1 - mp);
        ctx.fillStyle = mp < 0.5 ? "#fef9c3" : "#c084fc";
        ctx.shadowColor = "#e879f9";
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(mx, my, m.size * (1 - mp * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      // ── Phase 3: fade out afterglow ──
      const p = (t - ACTIVE_END) / (FADE_END - ACTIVE_END);
      const alpha = 1 - Easing.easeInQuad(p);
      const w = 10 * (1 - p) + 2;

      ctx.globalAlpha = alpha * 0.4;
      ctx.fillStyle = "#a855f7";
      ctx.shadowColor = "#c084fc";
      ctx.shadowBlur = 20;
      ctx.fillRect(0, -w, len, w * 2);

      ctx.globalAlpha = alpha * 0.7;
      ctx.fillStyle = "#fef9c3";
      ctx.shadowBlur = 6;
      ctx.fillRect(0, -1, len, 2);

      // Lingering motes
      for (const m of this.motes) {
        const age = t - m.t;
        if (age < 0 || age > m.life) continue;
        const mp = age / m.life;
        const mx = m.dist * len;
        const my = m.ox + (m.speed * age) / 1000;
        ctx.globalAlpha = m.alpha * (1 - mp) * alpha;
        ctx.fillStyle = "#c084fc";
        ctx.shadowColor = "#a855f7";
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.arc(mx, my, m.size * (1 - mp * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL_MS;
  }
}
